import React, {useContext, useEffect} from 'react';
import {useSelector} from 'react-redux';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {selectUserActivity} from 'src/modules/profile/user/services/user-profile-slice';
import {getUserActivity} from 'src/openapi/admin/apis/GetUserActivityApi';
import {useAppDispatch} from 'src/store/hooks';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import DiffTime from '@pb/kit-component-diff-time';
import {FormSection} from '@pb/kit-component-form-section';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Notify} from '@pb/kit-component-notify';
import {Tooltip} from '@pb/kit-component-popups';
import {LocaleContext} from '@pb/kit-context-locale';

import {UserAuthInfoLabel} from './authentication-status/UserAuthInfoLabel';
import {UserActivitySkeleton} from './user-activity.skeleton';

import {userActivityHeader, userActivityOnlineLabel, userActivityOnlineNotify} from './user-activity.module.styl';

type Props = {
    userId: number,
};

export const UserActivity = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();
    const activityResponse = useSelector(selectUserActivity);

    const {locale} = useContext(LocaleContext);
    const activity = activityResponse?.data;
    const loadingError = activityResponse?.error || null;

    useEffect(() => {
        const fetchUserActivity = () => apiDispatch(getUserActivity({userId}));

        fetchUserActivity();
        const intervalId = setInterval(fetchUserActivity, 600_000);

        return () => {
            clearInterval(intervalId);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Skeleton shown={!activityResponse} view={<UserActivitySkeleton/>}>
            <ServerResponseHandler serverError={loadingError} smallView={true} sectionName="User Activity">
                {activity ? (
                    <FormSection
                        headline="User Activity"
                        info={headline => (
                            <Item grow={1}>
                                <span className={userActivityHeader}>
                                    {headline}
                                    <UserAuthInfoLabel userId={userId}/>
                                </span>
                            </Item>
                        )}>
                        <Group direction="v" gap="m">
                            <Group direction="h" gap="m">
                                <strong style={{width: 230}}>Last Platform Active</strong>
                                <Group direction="h" gap="m" style={{width: 140}}>
                                    {!activity.lastPlatformActive && !activity.platformOnline && '--'}
                                    {!!activity.lastPlatformActive && !activity.platformOnline && (
                                        <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                                            <div ref={anchorRef} {...getAnchorProps()}>
                                                <DiffTime
                                                    createdDate={activity.lastPlatformActive}
                                                    fullUnits={true}
                                                />
                                            </div>
                                        )}>
                                            {FormattedDate.format({
                                                value: activity.lastPlatformActive,
                                                format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE_TIME')},
                                            )}
                                        </Tooltip>
                                    )}
                                    {activity.platformOnline ? (
                                        <Group direction="h" alignItems="center" gap="s">
                                            <Notify mix={userActivityOnlineNotify}/>
                                            <strong className={userActivityOnlineLabel}>
                                                Online now
                                            </strong>
                                        </Group>
                                    ) : null}
                                </Group>
                            </Group>
                            <Group direction="h" gap="m">
                                <strong style={{width: 230}}>Last Mobile Active</strong>
                                <Group direction="h" gap="m" style={{width: 140}}>
                                    {!activity.lastMobileActivity && !activity.mobileOnline && '--'}
                                    {!!activity.lastMobileActivity && !activity.mobileOnline && (
                                        <Tooltip anchor={({anchorRef, getAnchorProps}) => (
                                            <div ref={anchorRef} {...getAnchorProps()}>
                                                <DiffTime
                                                    createdDate={activity.lastMobileActivity}
                                                    fullUnits={true}
                                                />
                                            </div>
                                        )}>
                                            {FormattedDate.format({
                                                value: activity.lastMobileActivity,
                                                format: mapDateFormat(locale.dateFormat, 'WORD', 'DATE_TIME')},
                                            )}
                                        </Tooltip>
                                    )}
                                    {activity.mobileOnline ? (
                                        <Group direction="h" alignItems="center" gap="s">
                                            <Notify mix={userActivityOnlineNotify}/>
                                            <strong className={userActivityOnlineLabel}>
                                                Online now
                                            </strong>
                                        </Group>
                                    ) : null}
                                </Group>
                            </Group>
                        </Group>
                    </FormSection>
                ) : <div/>}
            </ServerResponseHandler>
        </Skeleton>
    );
};
